import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const TC_TEXT =
  'By registering I acknowledge that if I win a jackpot using a pick generated by this platform, I agree to donate 10% of my winnings to the platform operator. This service is provided free of charge.';

// Public Cloudflare Turnstile site key (shared my-investments.io widget), baked at build.
// Empty → widget not rendered and no token sent (backend skips verification too).
const TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? '';

export default function Register() {
  const nav = useNavigate();
  const location = useLocation();
  const locationState = location.state as { step?: 'verify'; email?: string } | null;
  const [email, setEmail] = useState(locationState?.email ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [tcAccepted, setTcAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'verify'>(locationState?.step === 'verify' ? 'verify' : 'form');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Load + render the Cloudflare Turnstile widget on the register form.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const ts = () => (window as unknown as { turnstile?: any }).turnstile;
    // Leaving the form (e.g. → verify, then "← Back") unmounts the widget's div.
    // Tear down the old widget so a return to the form re-renders a fresh one.
    if (step !== 'form') {
      if (widgetIdRef.current) { try { ts()?.remove(widgetIdRef.current); } catch { /* noop */ } }
      widgetIdRef.current = null;
      return;
    }
    function render() {
      if (!ts() || !turnstileRef.current || widgetIdRef.current) return;
      widgetIdRef.current = ts().render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (t: string) => setTurnstileToken(t),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
    }
    if (ts()) { render(); return; }
    const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    let s = document.querySelector<HTMLScriptElement>('script[src^="https://challenges.cloudflare.com/turnstile"]');
    if (!s) {
      s = document.createElement('script');
      s.src = SRC; s.async = true; s.defer = true;
      document.head.appendChild(s);
    }
    s.addEventListener('load', render);
    return () => s?.removeEventListener('load', render);
  }, [step]);

  function resetTurnstile() {
    setTurnstileToken('');
    (window as unknown as { turnstile?: any }).turnstile?.reset(widgetIdRef.current ?? undefined);
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setAlreadyRegistered(false);
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (!tcAccepted) { setError('You must accept the Terms & Conditions.'); return; }
    if (TURNSTILE_SITE_KEY && !turnstileToken) { setError('Please complete the captcha.'); return; }
    setLoading(true);
    try {
      const r = await fetch('/api/v1/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, tc_accepted: true, turnstile_token: turnstileToken || null }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.detail ?? 'Registration failed.');
        if (r.status === 409) setAlreadyRegistered(true);
        resetTurnstile();  // tokens are single-use — let the user retry
        return;
      }
      setStep('verify');
    } catch {
      setError('Network error. Please try again.');
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setOtpError('');
    setOtpLoading(true);
    try {
      const r = await fetch('/api/v1/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, otp }),
      });
      const data = await r.json();
      if (!r.ok) { setOtpError(data.detail ?? 'Invalid code.'); return; }
      nav('/profile');
    } catch {
      setOtpError('Network error.');
    } finally {
      setOtpLoading(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setResent(false);
    try {
      await fetch('/api/v1/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setResent(true);
    } finally {
      setResending(false);
    }
  }

  if (step === 'verify') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
          <button
            onClick={() => setStep('form')}
            className="mb-6 block text-xs text-slate-400 hover:text-slate-600"
          >
            ← Back
          </button>
          <h1 className="mb-1 text-2xl font-bold text-slate-900">Verify your email</h1>
          <p className="mb-6 text-sm text-slate-500">
            We sent a 6-digit code to <strong>{email}</strong>. Enter it below.
          </p>
          <form onSubmit={handleVerify} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit code"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-sky-500"
              required
            />
            {otpError && <p className="text-sm text-rose-600">{otpError}</p>}
            <button
              type="submit"
              disabled={otpLoading || otp.length < 6}
              className="w-full rounded-full bg-sky-600 py-3 font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-50"
            >
              {otpLoading ? 'Verifying…' : 'Verify & sign in'}
            </button>
          </form>
          <div className="mt-4 text-center">
            {resent ? (
              <p className="text-xs text-green-600">Code resent — check your inbox.</p>
            ) : (
              <button
                onClick={handleResend}
                disabled={resending}
                className="text-xs text-slate-400 hover:text-slate-600 disabled:opacity-50"
              >
                {resending ? 'Sending…' : "Didn't receive a code? Resend"}
              </button>
            )}
          </div>
          <p className="mt-2 text-center text-xs text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-sky-600 hover:text-sky-800">Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
        <Link to="/" className="mb-6 block text-xs text-slate-400 hover:text-slate-600">← Back to picks</Link>
        <h1 className="mb-1 text-2xl font-bold text-slate-900">Create an account</h1>
        <p className="mb-6 text-sm text-slate-500">Track your picks, compare them to real results, and unlock premium strategies.</p>
        <form onSubmit={handleRegister} className="space-y-4">
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            required
            autoComplete="email"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            required
            minLength={8}
          />
          <input
            type="password"
            placeholder="Confirm password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            required
          />
          <label className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <input
              type="checkbox"
              checked={tcAccepted}
              onChange={(e) => setTcAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 flex-shrink-0 rounded"
            />
            <span>{TC_TEXT}</span>
          </label>
          {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="flex justify-center" />}
          {error && (
            <div className="text-sm text-rose-600">
              {error}
              {alreadyRegistered && (
                <span className="mt-1 block text-slate-600">
                  <Link to="/login" className="font-semibold text-sky-600 hover:text-sky-800">Sign in</Link>
                  {' · '}
                  <Link to="/forgot-password" className="font-semibold text-sky-600 hover:text-sky-800">Forgot password?</Link>
                </span>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !tcAccepted}
            className="w-full rounded-full bg-sky-600 py-3 font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:opacity-50"
          >
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <p className="mt-5 text-center text-sm text-slate-500">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-sky-600 hover:text-sky-800">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
